class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productFormId = this.dataset.productFormId;
    this.button = this.querySelector('[data-sticky-atc-submit]');
    this.priceEl = this.querySelector('[data-sticky-atc-price]');
    this.compareEl = this.querySelector('[data-sticky-atc-compare]');
    this.labelEl = this.querySelector('[data-sticky-atc-label]');
  }

  connectedCallback() {
    this.setupScrollObserver();
    this.button?.addEventListener('click', this.onClick.bind(this));
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        () => this.setLoading(false)
      );
      this.cartErrorUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartError,
        () => this.setLoading(false)
      );
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.variantChangeUnsubscriber?.();
    this.cartUpdateUnsubscriber?.();
    this.cartErrorUnsubscriber?.();
  }

  setupScrollObserver() {
    const mainForm = document.getElementById(this.productFormId);
    if (!mainForm || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Only show sticky when the user has scrolled *past* the main form,
        // not when it's still below the viewport on initial load.
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.setVisible(scrolledPast);
      },
      { threshold: 0 }
    );

    this.observer.observe(mainForm);
  }

  setVisible(visible) {
    this.classList.toggle('sticky-atc--visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    document.body.classList.toggle('sticky-atc-is-visible', visible);
  }

  onClick(event) {
    event.preventDefault();
    if (this.button.disabled) return;
    const mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (mainSubmit && !mainSubmit.disabled) {
      this.setLoading(true);
      mainSubmit.click();
      // Fallback: if for some reason no cart event fires, don't leave the
      // button stuck in loading state forever.
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = setTimeout(() => this.setLoading(false), 6000);
    }
  }

  setLoading(loading) {
    if (!this.button) return;
    this.button.classList.toggle('sticky-atc__button--loading', loading);
    if (loading) {
      this.button.disabled = true;
    } else {
      clearTimeout(this.loadingTimeout);
      this.syncButtonFromDOM();
    }
  }

  onVariantChange(event) {
    if (event?.data?.sectionId !== this.sectionId) return;

    // The main product DOM is already patched by product-info.js by the time
    // this fires, so we can read the current state directly.
    requestAnimationFrame(() => {
      this.syncPriceFromDOM();
      this.syncButtonFromDOM();
    });
  }

  syncPriceFromDOM() {
    const priceBlock = document.querySelector(`#price-${this.sectionId} .price`);
    if (!priceBlock) return;

    const onSale = priceBlock.classList.contains('price--on-sale');
    let current = '';
    let compare = '';

    if (onSale) {
      const sale = priceBlock.querySelector('.price__sale .price-item--sale');
      const compareEl = priceBlock.querySelector('.price__sale s.price-item--regular');
      if (sale) current = sale.textContent.trim();
      if (compareEl) compare = compareEl.textContent.trim();
    } else {
      const regular = priceBlock.querySelector('.price__regular .price-item--regular');
      if (regular) current = regular.textContent.trim();
    }

    if (current && this.priceEl) this.priceEl.textContent = current;
    if (this.compareEl) {
      if (compare) {
        this.compareEl.textContent = compare;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.textContent = '';
        this.compareEl.hidden = true;
      }
    }
  }

  syncButtonFromDOM() {
    const mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (!mainSubmit || !this.button) return;

    this.button.disabled = mainSubmit.hasAttribute('disabled');
    const mainLabel = mainSubmit.querySelector('span');
    if (mainLabel && this.labelEl) {
      this.labelEl.textContent = mainLabel.textContent.trim();
    }
  }
}

customElements.define('sticky-atc', StickyAtc);
